import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/storage"; // Import compat storage
import { initializeFirestore, memoryLocalCache, getFirestore } from "firebase/firestore";
import config from "../firebase-applet-config.json";

// Detect if we are in Local Mock Storage Mode (no real Firebase key configured)
export let isLocalMode = !config.apiKey || config.apiKey === "remixed-api-key" || config.apiKey.startsWith("remixed-");

export function setLocalMode(value: boolean) {
  isLocalMode = value;
  if (value) {
    (firebase as any).auth = () => mockAuthInstance;
  }
}

// Safe configuration proxy to prevent Firebase SDK from failing format check synchronously on bootstrap
const safeConfig = { ...config };
if (isLocalMode) {
  // A syntactically valid mock Google API key pattern (exactly 39 chars, starts with AIzaSy)
  safeConfig.apiKey = "AIzaSyA1B2C3D4E5F6G7H8I9J0K1L2M3N4O5P6Q";
}

// Initialize Firebase (Singleton pattern)
const app = !firebase.apps.length ? firebase.initializeApp(safeConfig) : firebase.app();

// Initialize Firestore (Modular)
let db: any;
const databaseId = (safeConfig as any).firestoreDatabaseId || "(default)";

try {
  db = initializeFirestore(app as any, {
    localCache: memoryLocalCache()
  }, databaseId);
} catch (e) {
  db = getFirestore(app as any, databaseId);
}

// Initialize Storage (Compat)
const storage = firebase.storage();

// Mock Auth Class for Local Storage Mode
class MockAuth {
  private listeners: ((user: any) => void)[] = [];
  public currentUser: any = null;

  constructor() {
    // Load persisted mock session if exists
    const storedUser = localStorage.getItem("mock_auth_user");
    if (storedUser) {
      try {
        this.currentUser = JSON.parse(storedUser);
      } catch (e) {
        this.currentUser = null;
      }
    }
  }

  onAuthStateChanged(callback: (user: any) => void) {
    this.listeners.push(callback);
    // Trigger callback immediately with current user
    setTimeout(() => {
      callback(this.currentUser);
    }, 100);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  async signInWithEmailAndPassword(email: string, _: string) {
    const uid = "mock-uid-" + email.replace(/[^a-zA-Z0-9]/g, "-");
    const user = {
      uid,
      email,
      emailVerified: true,
      isAnonymous: false,
      providerData: [{ providerId: "password", email }]
    };
    this.currentUser = user;
    localStorage.setItem("mock_auth_user", JSON.stringify(user));
    
    // Notify listeners
    this.listeners.forEach(l => l(user));
    
    return { user };
  }

  async createUserWithEmailAndPassword(email: string, _: string) {
    const uid = "mock-uid-" + email.replace(/[^a-zA-Z0-9]/g, "-");
    const user = {
      uid,
      email,
      emailVerified: true,
      isAnonymous: false,
      providerData: [{ providerId: "password", email }]
    };
    this.currentUser = user;
    localStorage.setItem("mock_auth_user", JSON.stringify(user));
    
    // Notify listeners
    this.listeners.forEach(l => l(user));
    
    return { user };
  }

  async signOut() {
    this.currentUser = null;
    localStorage.removeItem("mock_auth_user");
    this.listeners.forEach(l => l(null));
  }
}

const mockAuthInstance = new MockAuth();

// Fail-safe dynamically switching AuthWrapper
class AuthWrapper {
  get currentUser() {
    return isLocalMode ? mockAuthInstance.currentUser : firebase.auth().currentUser;
  }
  onAuthStateChanged(callback: (user: any) => void) {
    if (isLocalMode) {
      return mockAuthInstance.onAuthStateChanged(callback);
    }
    try {
      return firebase.auth().onAuthStateChanged(callback);
    } catch (e) {
      isLocalMode = true;
      return mockAuthInstance.onAuthStateChanged(callback);
    }
  }
  async signInWithEmailAndPassword(email: string, pass: string) {
    if (isLocalMode) {
      return mockAuthInstance.signInWithEmailAndPassword(email, pass);
    }
    try {
      return await firebase.auth().signInWithEmailAndPassword(email, pass);
    } catch (e: any) {
      if (e.message && (e.message.includes('auth') || e.message.includes('API') || e.message.includes('disabled'))) {
        isLocalMode = true;
        return mockAuthInstance.signInWithEmailAndPassword(email, pass);
      }
      throw e;
    }
  }
  async createUserWithEmailAndPassword(email: string, pass: string) {
    if (isLocalMode) {
      return mockAuthInstance.createUserWithEmailAndPassword(email, pass);
    }
    try {
      return await firebase.auth().createUserWithEmailAndPassword(email, pass);
    } catch (e: any) {
      if (e.message && (e.message.includes('auth') || e.message.includes('API') || e.message.includes('disabled'))) {
        isLocalMode = true;
        return mockAuthInstance.createUserWithEmailAndPassword(email, pass);
      }
      throw e;
    }
  }
  async signOut() {
    if (isLocalMode) {
      return mockAuthInstance.signOut();
    }
    try {
      return await firebase.auth().signOut();
    } catch (e) {
      isLocalMode = true;
      return mockAuthInstance.signOut();
    }
  }
}

// Export proper auth & getSecondaryApp handlers
export const auth = new AuthWrapper() as any;

if (isLocalMode) {
  // Support compat imports
  (firebase as any).auth = () => mockAuthInstance;
  (firebase.auth as any).EmailAuthProvider = {
    credential: (email: string, _: string) => ({ email })
  };
}

export { db, storage };

export const getSecondaryApp = () => {
  if (isLocalMode) {
    return {
      auth: () => ({
        createUserWithEmailAndPassword: async (email: string, _: string) => {
          const uid = "mock-uid-" + email.replace(/[^a-zA-Z0-9]/g, "-");
          return { user: { uid, email } };
        },
        signOut: async () => {},
      }),
      delete: async () => {}
    } as any;
  }
  return firebase.initializeApp(safeConfig, "SecondaryApp");
}
